import {
  createContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import { useResponsive } from 'commons/components';
import { AgentChatActionBus } from 'commons/services/AgentChatService';
import { AGENT_ENABLED_ROUTES } from './config';
import {
  AgentChatPanelContextType,
  AgentChatPanelContextProviderProps,
} from './types';

const defaultContextValue: AgentChatPanelContextType = {
  isOpen: false,
  openAgent: () => void 0,
  closeAgent: () => void 0,
  shouldRenderOnPage: false,
  actionBus: null,
};

export const AgentChatPanelContext =
  createContext<AgentChatPanelContextType>(defaultContextValue);

/**
 * AgentChatPanelContextProvider manages the global state of the AI chat side panel and the action bus.
 * This context controls when the chat panel is open/closed and provides the AgentChatActionBus instance to all child components.
 * The action bus is only instantiated on desktop platform.
 */
export function AgentChatPanelContextProvider({
  children,
}: AgentChatPanelContextProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useResponsive();
  const location = useLocation();
  const busRef = useRef<AgentChatActionBus | null>(null);
  const menuStateBeforeAgentChatRef = useRef<boolean | null>(null);
  const { isExpandedMenu, expandMenu, collapseMenu } = useDesktopMenuContext();

  const isInAllowedRoute = useCallback(
    (pathName: string) =>
      AGENT_ENABLED_ROUTES.some((route) => {
        const routePattern = new RegExp(`^${route.replace(/:\w+/g, '\\d+')}$`);
        return routePattern.test(pathName);
      }),
    [],
  );

  const shouldRenderOnPage = useMemo(
    () => isInAllowedRoute(location.pathname),
    [isInAllowedRoute, location.pathname],
  );

  const openAgent = useCallback(() => {
    menuStateBeforeAgentChatRef.current = isExpandedMenu;
    collapseMenu();
    setIsOpen(true);
  }, [isExpandedMenu, collapseMenu]);

  const closeAgent = useCallback(() => {
    if (menuStateBeforeAgentChatRef.current !== null) {
      if (menuStateBeforeAgentChatRef.current) {
        expandMenu();
      }
      menuStateBeforeAgentChatRef.current = null;
    }
    setIsOpen(false);
  }, [expandMenu]);

  if (!busRef.current && !isMobile) {
    busRef.current = new AgentChatActionBus();
  }

  // Cleanup when mobile (not yet supported) and when dismounted
  if (isMobile && busRef.current) {
    busRef.current.dispose();
    busRef.current = null;
  }
  useEffect(
    () => () => {
      busRef.current?.dispose();
      busRef.current = null;
    },
    [],
  );

  // Ensure the action bus is only available on desktop
  const actionBus = isMobile ? null : busRef.current;

  const value = useMemo(
    () => ({
      isOpen,
      openAgent,
      closeAgent,
      shouldRenderOnPage,
      actionBus,
    }),
    [isOpen, openAgent, closeAgent, shouldRenderOnPage, actionBus],
  );

  return (
    <AgentChatPanelContext.Provider value={value}>
      {children}
    </AgentChatPanelContext.Provider>
  );
}
